/**
 * The desk: geometry for the sheets lying on it, the sample documents that
 * show Typora rendering, and the stationery scattered between them.
 *
 * Every sheet is authored at SHEET_WIDTH × SHEET_HEIGHT and only transformed
 * into its slot, so swapping two sheets is a single transform transition with
 * no reflow.
 */

export const SHEET_WIDTH = 640;
export const SHEET_HEIGHT = 860;

export type SheetId =
  | "letter"
  | "features"
  | "quickstart"
  | "reference"
  | "release"
  | "harbor"
  | "credits"
  | "study";

export type SlotName =
  | "front"
  | "top"
  | "leftA"
  | "leftB"
  | "leftC"
  | "rightA"
  | "rightB"
  | "rightC";

export type SlotGeometry = {
  x: number;
  y: number;
  rotate: number;
  scale: number;
  z: number;
};

export type DeskLayout = {
  slots: Record<SlotName, SlotGeometry>;
  titleTop: number;
  frontTop: number;
  stationery: {
    pin: { left: number; top: number };
    clip: { left: number; top: number };
    polaroid: { left: number; top: number };
    keyboard: { left: number; top: number };
    sticky: { left: number; top: number };
    pencil: { left: number; top: number };
  };
};

/**
 * Air kept between the slogan and the front sheet, hovered or not, so lifting
 * the sheet never crowds the title block.
 */
export const HERO_CLEARANCE = 32;

export const DEFAULT_ASSIGNMENT: Record<SlotName, SheetId> = {
  front: "letter",
  top: "release",
  leftA: "harbor",
  leftB: "reference",
  leftC: "quickstart",
  rightA: "features",
  rightB: "study",
  rightC: "credits",
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Positions everything for a stage of `width` × `height`. The 1440 × 900
 * design is the reference; side sheets stay anchored to their edge and the
 * centre column tracks the middle, so the composition holds on other sizes.
 */
export function layoutDesk(width: number, height: number): DeskLayout {
  const cx = width / 2;
  const titleTop = clamp(height * 0.16, 108, 148);
  // The gap under the hero holds the title block, its clearance, and the room
  // a hovered front sheet needs to rise into.
  const frontTop = titleTop + 214;
  const topSheetBottom = titleTop - 26;

  const leftA = { x: Math.min(56, cx - 638), y: titleTop - 60 };
  const rightA = { x: Math.max(width - 408, cx + 282), y: titleTop - 50 };
  // On a wide desk the side sheets stay at their edge and a gap opens beside
  // the letter. Two more sheets slide out into it, and tuck back under the
  // letter as the gap closes.
  const spare = clamp(cx - 734, 0, 260);

  return {
    titleTop,
    frontTop,
    slots: {
      front: { x: cx - SHEET_WIDTH / 2, y: frontTop, rotate: 0, scale: 1, z: 6 },
      top: {
        x: cx - 158,
        y: topSheetBottom - SHEET_HEIGHT * 0.5,
        rotate: 1.5,
        scale: 0.5,
        z: 2,
      },
      leftA: { ...leftA, rotate: -4, scale: 0.56, z: 4 },
      leftB: { x: -8, y: frontTop + 328, rotate: 3, scale: 0.6, z: 2 },
      // Sits on top of the sheet beside it, so its heading stays readable…
      leftC: {
        x: cx - 432 - spare * 0.58,
        y: frontTop - 34,
        rotate: -5,
        scale: 0.5,
        z: 5,
      },
      rightA: { ...rightA, rotate: 3.5, scale: 0.56, z: 4 },
      rightB: {
        x: Math.max(width - 312, cx + 400),
        y: frontTop + 264,
        rotate: -3,
        scale: 0.6,
        z: 2,
      },
      // …while this one slides under, filling the gap without adding a corner.
      rightC: {
        x: cx + 100 + spare * 0.735,
        y: frontTop + 30,
        rotate: 4,
        scale: 0.5,
        z: 3,
      },
    },
    stationery: {
      pin: { left: 44, top: 22 },
      clip: { left: leftA.x + 262, top: leftA.y - 54 },
      polaroid: { left: 40, top: titleTop + 326 },
      // Right at the near edge of the desk: only its top rows are on screen,
      // the way a keyboard sits in front of you, half out of frame.
      keyboard: { left: 150 + spare * 0.35, top: height - 82 },
      sticky: { left: width - 184, top: frontTop + 98 },
      pencil: { left: cx + 354, top: frontTop + 254 },
    },
  };
}

/**
 * Caps a hover lift at the room between the front sheet and the hero, so the
 * title and slogan keep their clearance however tall the viewport is.
 */
export function clampFrontLift(
  lift: number,
  layout: DeskLayout,
  heroHeight: number,
): number {
  const headroom = layout.frontTop - (layout.titleTop + heroHeight) - HERO_CLEARANCE;
  return Math.max(0, Math.min(lift, headroom));
}

export function slotTransform(slot: SlotGeometry): string {
  return `translate(${slot.x}px, ${slot.y}px) rotate(${slot.rotate}deg) scale(${slot.scale})`;
}

/* ------------------------------------------------------------------ */
/* Sample documents                                                    */
/* ------------------------------------------------------------------ */

function TaskCheck() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3.5 8.5 2.8 2.8L12.5 5" />
    </svg>
  );
}

function Hash() {
  return (
    <span className="md-hash" aria-hidden="true">
      #
    </span>
  );
}

export function HarborSheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        Harbor, early
      </h1>
      <p>
        The fog lifted a little after nine. Two boats came in late, the smaller
        one riding low with the morning's catch. Nobody hurried.
      </p>
      <blockquote>
        <p>The trick is to write it down before it turns into a story.</p>
      </blockquote>
      <figure>
        <svg
          width="520"
          height="190"
          viewBox="0 0 520 190"
          className="doc-image"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="harborSky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#e3e9ec" />
              <stop offset="1" stopColor="#f4f0e7" />
            </linearGradient>
          </defs>
          <rect width="520" height="190" fill="url(#harborSky)" />
          <circle cx="392" cy="58" r="20" fill="#f0dcae" opacity="0.9" />
          <path
            d="M0 118 C80 104 150 110 230 116 C320 124 400 106 520 112 V190 H0 Z"
            fill="#b9c2bd"
          />
          <path
            d="M0 138 C110 130 200 146 300 138 C380 132 450 142 520 136 V190 H0 Z"
            fill="#8e9994"
          />
          <rect x="0" y="152" width="520" height="38" fill="#6f7a79" />
          <rect x="300" y="132" width="150" height="6" fill="#4b5563" />
          <path
            d="M40 162 h40 M120 170 h26 M200 164 h50 M330 172 h30"
            stroke="#a5b0ad"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path d="M92 150 l10 -22 l10 22 Z" fill="#374151" />
          <rect x="86" y="150" width="32" height="6" fill="#374151" />
        </svg>
        <figcaption>Two gulls on the breakwater, one facing the wrong way.</figcaption>
      </figure>
      <ul className="task">
        <li>
          <span className="cb is-on">
            <TaskCheck />
          </span>
          <span className="is-done">Find out who owns the blue-hulled trawler</span>
        </li>
        <li>
          <span className="cb" />
          <span>Ferry timetable for the north pier</span>
        </li>
      </ul>
    </div>
  );
}

export function QuickStartSheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        Quick start
      </h1>
      <p>
        Type markdown and read the result. There is no preview pane, because
        there is nothing to preview: the page you write in is the page you get.
      </p>
      <figure>
        <svg
          width="520"
          height="190"
          viewBox="0 0 520 190"
          className="doc-image"
          aria-hidden="true"
        >
          <rect width="520" height="190" fill="#f3f4f6" />
          <rect x="0" y="0" width="520" height="26" fill="#e8eaed" />
          <circle cx="18" cy="13" r="4" fill="#c9ced6" />
          <circle cx="32" cy="13" r="4" fill="#c9ced6" />
          <circle cx="46" cy="13" r="4" fill="#c9ced6" />
          <rect x="0" y="26" width="132" height="164" fill="#eceef1" />
          <path
            d="M16 46 h74 M16 64 h58 M16 82 h66 M16 100 h48 M16 118 h70"
            stroke="#c3c8d0"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <rect x="10" y="74" width="112" height="16" rx="4" fill="#dfe3e8" />
          <rect x="132" y="26" width="388" height="164" fill="#ffffff" />
          <rect x="168" y="50" width="150" height="13" rx="3" fill="#3f4652" />
          <path
            d="M168 82 h300 M168 100 h286 M168 118 h312 M168 136 h214"
            stroke="#d5d9df"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <rect x="168" y="156" width="2" height="16" fill="#3f4652" />
        </svg>
        <figcaption>One window: the file tree, the outline, and the page.</figcaption>
      </figure>
      <h2>Three things to try</h2>
      <ol>
        <li>
          Start a line with <code>## </code> — the hashes fade as the heading
          forms under them.
        </li>
        <li>
          Paste an image, and tell Typora once in <em>Preferences → Images</em>
          {" "}where copies should land.
        </li>
        <li>
          Press <code>⌘/</code> to read the markup behind the page, then press
          it again to put it away.
        </li>
      </ol>
      <p>The rest is markdown you already know.</p>
    </div>
  );
}

export function ReferenceSheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        Markdown reference
      </h1>
      <p>
        The markup disappears as it is understood. What follows is what Typora
        listens for.
      </p>
      <table>
        <thead>
          <tr>
            <th>You type</th>
            <th>You get</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>**weight**</code>
            </td>
            <td>
              <strong>weight</strong>
            </td>
          </tr>
          <tr>
            <td>
              <code>*emphasis*</code>
            </td>
            <td>
              <em>emphasis</em>
            </td>
          </tr>
          <tr>
            <td>
              <code>`literal`</code>
            </td>
            <td>
              <code>literal</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>&gt; quoted</code>
            </td>
            <td>a quotation</td>
          </tr>
        </tbody>
      </table>
      <h2>Fenced code</h2>
      <pre>
        <code>
          <span className="kw">export function</span>{" "}
          <span className="fn">render</span>(doc) {"{"}
          {"\n"}
          {"  "}
          <span className="kw">return</span> doc.blocks.
          <span className="fn">map</span>(paint);{"\n"}
          {"}"}
        </code>
      </pre>
      <h2>Math</h2>
      <p>
        Two dollar signs open a block; the formula sets itself as the second
        pair closes it.
      </p>
      <div className="math">∫₀^∞ e^(−x²) dx = √π ⁄ 2</div>
      <h2>Diagrams</h2>
      <p>
        A fence tagged <code>mermaid</code> draws itself the moment it closes.
      </p>
      <figure>
        <svg
          width="520"
          height="120"
          viewBox="0 0 520 120"
          className="doc-image"
          aria-hidden="true"
        >
          <rect width="520" height="120" fill="#fbfbfa" />
          <g fill="#eef2f6" stroke="#9aa6b4" strokeWidth="1.5">
            <rect x="28" y="38" width="118" height="46" rx="6" />
            <rect x="201" y="38" width="118" height="46" rx="6" />
            <rect x="374" y="38" width="118" height="46" rx="6" />
          </g>
          <g
            fill="#374151"
            fontFamily="Helvetica, Arial, sans-serif"
            fontSize="15"
            textAnchor="middle"
          >
            <text x="87" y="66">draft</text>
            <text x="260" y="66">edit</text>
            <text x="433" y="66">ship</text>
          </g>
          <g stroke="#6b7280" strokeWidth="1.5" fill="none">
            <path d="M146 61 h44" />
            <path d="M319 61 h44" />
          </g>
          <g fill="#6b7280">
            <path d="M190 61 l-8 -4 v8 Z" />
            <path d="M363 61 l-8 -4 v8 Z" />
          </g>
        </svg>
        <figcaption>Flowchart, sequence and Gantt blocks all work this way.</figcaption>
      </figure>
    </div>
  );
}

export function ReleaseNotesSheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        What's new in 1.14
      </h1>
      <p className="footnote">Published 19 July 2026</p>
      <h2>New features</h2>
      <ul>
        <li>
          <strong>Floating editor toolbar.</strong> Enable it from{" "}
          <em>menubar → View → Toolbar</em>, or from{" "}
          <em>Settings → Appearance</em>.
        </li>
        <li>
          <strong>Sidebar file configuration.</strong> Show hidden files and
          folders, and choose whether non-markdown files are listed at all.
        </li>
      </ul>
      <h2>Improvements</h2>
      <ul>
        <li>
          macOS gets the <strong>context menu for setting styles</strong>, just
          like the Windows and Linux versions.
        </li>
        <li>The file tree can be walked from the keyboard.</li>
      </ul>
      <h2>Bug fixes</h2>
      <ul className="task">
        <li>
          <span className="cb is-on">
            <TaskCheck />
          </span>
          <span className="is-done">A list rendering regression</span>
        </li>
        <li>
          <span className="cb is-on">
            <TaskCheck />
          </span>
          <span className="is-done">List items on macOS 27</span>
        </li>
        <li>
          <span className="cb is-on">
            <TaskCheck />
          </span>
          <span className="is-done">CORS handling</span>
        </li>
        <li>
          <span className="cb is-on">
            <TaskCheck />
          </span>
          <span className="is-done">Export on macOS</span>
        </li>
      </ul>
      <hr />
      <p className="footnote">
        Every release is written up the same way, at{" "}
        <a href="https://support.typora.io" target="_blank" rel="noreferrer">
          support.typora.io
        </a>
        .
      </p>
    </div>
  );
}

export function CreditsSheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        Acknowledgement
      </h1>
      <p>
        Typora is developed by <strong>@LeeAbner</strong>. It stands on other
        people's work, and on readers who gave their time to it.
      </p>
      <h2>Localization</h2>
      <p>
        Twenty-nine languages, all contributed: Simplified and Traditional
        Chinese, Japanese, Korean, French, German, Spanish, Portuguese, Italian,
        Russian, Polish, Czech, Greek, Croatian, Hungarian, Galician,
        Indonesian, Vietnamese, Swedish, Catalan, Danish, Persian, Dutch,
        Slovak, Ukrainian, Turkish.
      </p>
      <h2>Rendering</h2>
      <ul>
        <li>
          <strong>MathJax</strong> — mathematical typesetting. Apache 2.0
        </li>
        <li>
          <strong>CodeMirror</strong> — the code editor component. MIT
        </li>
        <li>
          <strong>Mermaid</strong>, <strong>flowchart.js</strong> and{" "}
          <strong>js-sequence</strong> — diagrams. MIT
        </li>
        <li>
          <strong>DOMPurify</strong> — sanitising the HTML. Apache 2.0
        </li>
      </ul>
      <h2>The app around it</h2>
      <ul>
        <li>
          <strong>Electron</strong> — the desktop shell. MIT
        </li>
        <li>
          <strong>PDF.js</strong> — PDF rendering. Apache 2.0
        </li>
        <li>
          <strong>Sparkle</strong> — updates on macOS. MIT
        </li>
        <li>
          <strong>Font Awesome</strong> — icons. SIL OFL 1.1 / MIT
        </li>
      </ul>
      <hr />
      <p className="footnote">
        Everyone and everything, named in full, at{" "}
        <a
          href="https://support.typora.io/Acknowledgement/"
          target="_blank"
          rel="noreferrer"
        >
          support.typora.io/Acknowledgement
        </a>
        .
      </p>
    </div>
  );
}

export function StudySheet() {
  return (
    <div className="doc">
      <h1>
        <Hash />
        Gradient descent
      </h1>
      <p>
        Each step moves against the slope. The learning rate <code>η</code>{" "}
        decides how far, and that one number is most of the tuning.
      </p>
      <div className="math">θ ← θ − η ∇J(θ)</div>
      <pre>
        <code>
          <span className="kw">for</span> step <span className="kw">in</span>{" "}
          <span className="fn">range</span>(epochs):{"\n"}
          {"    "}grad = <span className="fn">gradient</span>(loss, theta){"\n"}
          {"    "}theta -= lr * grad{"\n"}
          {"    "}
          <span className="kw">if</span> <span className="fn">abs</span>(grad).
          <span className="fn">max</span>() &lt; tol:{"\n"}
          {"        "}
          <span className="kw">break</span>
        </code>
      </pre>
      <p>
        Too large a rate and the loss oscillates; too small and it crawls. Plot
        both before choosing, and note the rate next to the plot.
      </p>
      <h2>Momentum</h2>
      <p>
        Keep a running average of past gradients so a noisy step does not undo
        the last ten. The ball rolls through small bumps instead of stopping at
        each one.
      </p>
    </div>
  );
}

/** Blurred behind the lock until the visitor subscribes; the Kiiku board replaces it. */
export function BoardPlaceholder() {
  const rows = [
    ["Focus mode with typewriter scrolling", "Keep the current line anchored while the rest of the page fades.", "Planned"],
    ["Footnote popovers on hover", "Read a footnote in place instead of jumping to the bottom.", "Under review"],
    ["Custom export templates", "Ship a folder's worth of notes as one styled PDF or HTML site.", "Open"],
    ["Per-folder themes and fonts", "A journal folder that always opens in serif, a work folder that doesn't.", "Open"],
    ["Wiki-style links between notes", "Type two brackets, pick a file, get a link that survives renames.", "Open"],
  ] as const;
  const votes = [128, 96, 74, 61, 45];
  return (
    <div className="board" aria-hidden="true">
      <h1 className="board-title">Feature requests</h1>
      <p className="board-sub">
        Tell us what 2.0 should do, and vote on what others have asked for.
      </p>
      <div className="compose">
        <div className="compose-input">Describe a feature you'd like to see...</div>
        <span className="submit-btn board-post">Post</span>
      </div>
      <div className="req-list">
        {rows.map(([title, note, tag], index) => (
          <div className="req" key={title}>
            <span className="vote">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 14 6-6 6 6" />
              </svg>
              {votes[index]}
            </span>
            <div className="req-body">
              <p className="req-title">{title}</p>
              <p className="req-note">{note}</p>
            </div>
            <span className={`tag${tag === "Planned" ? " is-planned" : ""}`}>{tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stationery                                                          */
/* ------------------------------------------------------------------ */

type Placed = { left: number; top: number };

/**
 * The Typora icon, lying on the desk where the wordmark used to sit. Like the
 * tab on the letter, clicking it goes straight to the wait-list field.
 */
export function Sticker({ left, top, onActivate }: Placed & { onActivate: () => void }) {
  return (
    <button
      type="button"
      className="sticker"
      style={{ left, top }}
      onClick={onActivate}
      aria-label="Join the wait list"
    >
      <img src="/typora-icon.png" width={88} height={88} alt="Typora" decoding="async" />
    </button>
  );
}

export function PaperClip({ left, top }: Placed) {
  return (
    <svg
      className="clip"
      style={{ left, top }}
      width="26"
      height="66"
      viewBox="0 0 26 66"
      fill="none"
      stroke="#a3a8b0"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M7 12 V48 A6 6 0 0 0 19 48 V10 A5 5 0 0 0 9 10 V44 A3 3 0 0 0 15 44 V18" />
    </svg>
  );
}

export function Polaroid({ left, top }: Placed) {
  return (
    <div className="polaroid" style={{ left, top }} aria-hidden="true">
      <svg width="152" height="144" viewBox="0 0 152 144">
        <defs>
          <linearGradient id="photoSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#dfe6ea" />
            <stop offset="1" stopColor="#f3efe6" />
          </linearGradient>
        </defs>
        <rect width="152" height="144" fill="url(#photoSky)" />
        <circle cx="112" cy="44" r="13" fill="#f1dcae" opacity="0.9" />
        <path
          d="M0 92 C30 80 55 84 80 90 C110 97 130 86 152 88 V144 H0 Z"
          fill="#b8c0ba"
        />
        <path
          d="M0 108 C40 100 70 112 100 106 C125 101 140 108 152 104 V144 H0 Z"
          fill="#8f9a94"
        />
        <rect x="0" y="118" width="152" height="26" fill="#6f7a78" />
        <rect x="84" y="102" width="52" height="5" fill="#4b5563" />
        <path
          d="M14 126 h20 M44 132 h14 M100 128 h26"
          stroke="#a9b3b0"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      <span>north pier, fog</span>
    </div>
  );
}

function HandCheck() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 16 16"
      fill="none"
      stroke="#4b4636"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 8.5 3 3L13 4.5" />
    </svg>
  );
}

export function StickyNote({ left, top }: Placed) {
  return (
    <div className="sticky" style={{ left, top }} aria-hidden="true">
      <ul>
        <li>
          <span className="hand-box">
            <HandCheck />
          </span>
          <span>rewrite the editor</span>
        </li>
        <li>
          <span className="hand-box">
            <HandCheck />
          </span>
          <span>keep it silent</span>
        </li>
        <li>
          <span className="hand-box" />
          <span>ship 2.0</span>
        </li>
      </ul>
    </div>
  );
}

/**
 * A pale keyboard pushed to the side of the desk, half under the paper. Rows
 * are generated rather than drawn key by key: same shape, a tenth of the file.
 */
export function Keyboard({ left, top }: Placed) {
  const pad = 9;
  const keyW = 20;
  const keyH = 15;
  const gap = 3.5;
  /** Keys per row, and how far that row is stepped in — a keyboard's stagger. */
  const rows: Array<[count: number, indent: number]> = [
    [13, 0],
    [13, 4],
    [12, 10],
    [11, 18],
  ];
  const width = pad * 2 + 13 * keyW + 12 * gap;
  const height = pad * 2 + 5 * keyH + 4 * gap;
  const spaceY = pad + 4 * (keyH + gap);

  return (
    <svg
      className="keyboard"
      style={{ left, top }}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <rect
        width={width}
        height={height}
        rx="7"
        fill="#f1f1ee"
        stroke="#e3e3df"
        strokeWidth="1"
      />
      <g fill="#fafaf8" stroke="#e7e7e3" strokeWidth="0.9">
        {rows.map(([count, indent], row) =>
          Array.from({ length: count }, (_, key) => (
            <rect
              key={`${row}-${key}`}
              x={pad + indent + key * (keyW + gap)}
              y={pad + row * (keyH + gap)}
              width={keyW}
              height={keyH}
              rx="2.5"
            />
          )),
        )}
        <rect x={pad} y={spaceY} width={keyW * 1.6} height={keyH} rx="2.5" />
        <rect
          x={pad + keyW * 1.6 + gap}
          y={spaceY}
          width={width - pad * 2 - (keyW * 1.6 + gap) * 2}
          height={keyH}
          rx="2.5"
        />
        <rect
          x={width - pad - keyW * 1.6}
          y={spaceY}
          width={keyW * 1.6}
          height={keyH}
          rx="2.5"
        />
      </g>
    </svg>
  );
}

/**
 * A tab stuck to the letter's left edge, its arrow reaching over the paper to
 * the wait-list field. `top` is the field's centre, measured inside the sheet.
 * Clicking it brings the letter forward and puts the cursor in the field.
 */
export function WaitlistTag({ top, onActivate }: { top: number; onActivate: () => void }) {
  return (
    <button
      type="button"
      className="waitlist-tag"
      style={{ top }}
      onClick={onActivate}
      aria-label="Join the wait list"
    >
      <span aria-hidden="true">start here</span>
      <svg
        width="54"
        height="20"
        viewBox="0 0 54 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 14.5 C 13 15.5, 21 6.5, 41 8.5" />
        <path d="M34 3.5 L42.5 8.8 L34 14" />
      </svg>
    </button>
  );
}

export function Pencil({ left, top }: Placed) {
  return (
    <svg
      className="pencil"
      style={{ left, top }}
      width="22"
      height="300"
      viewBox="0 0 22 300"
      aria-hidden="true"
    >
      <rect x="3" y="0" width="16" height="22" rx="3" fill="#e2b6ad" />
      <rect x="2" y="20" width="18" height="14" fill="#c9cdd3" />
      <rect x="2" y="24" width="18" height="2" fill="#a9afb7" />
      <rect x="2" y="29" width="18" height="2" fill="#a9afb7" />
      <rect x="2" y="34" width="18" height="222" fill="#d8d3c4" />
      <rect x="2" y="34" width="6" height="222" fill="#cbc5b3" />
      <rect x="14" y="34" width="6" height="222" fill="#e4e0d3" />
      <path d="M2 256 L11 292 L20 256 Z" fill="#e8d5b0" />
      <path d="M8.5 282 L11 292 L13.5 282 Z" fill="#4b5563" />
    </svg>
  );
}
